/**
 * riskStratification.js
 * =====================
 * 
 * Shared constants, configurations, and utility functions for the Risk Stratification
 * feature. Used by both RiskStratificationPanel.js and RiskStratificationDialog.js
 * to ensure consistency across the application.
 * 
 * This module provides:
 * - TIER_CONFIG: Visual styling for each risk tier (1-4)
 * - DOMAIN_CONFIG: Configuration for risk domains (Safety, Function & Stability, Vulnerability)
 * - SCORE_LABELS: Styling for individual risk score levels
 * - RISK_LEVEL_OPTIONS: Options for manual review risk assignment
 * - DIALOG_DIMENSIONS: Standard dimensions for the risk stratification dialog
 * - Helper functions for mapping tiers/scores to labels and calculating stats
 */

import { ExclamationCircleIcon, ExclamationIcon, InformationCircleIcon } from '@heroicons/react/solid';

// ============================================================================
// TIER CONFIGURATION
// ============================================================================

/**
 * Tier configuration for the overall risk stratification.
 * 
 * Color scheme based on severity:
 *   - Urgent (Tier 1): Red - Immediate attention required
 *   - High (Tier 2): Orange - Priority attention needed
 *   - Medium (Tier 3): Yellow - Moderate concern
 *   - Standard (Tier 4): Green - Routine handling
 * 
 * Each tier includes colors for the gauge, background, border, and caption text.
 */
export const TIER_CONFIG = {
  1: {
    label: 'Urgent Priority',
    captionColor: '#dc2626',   // red-600 for caption text
    segmentColor: '#ef4444',   // red-500 for gauge segments
    bgColor: '#fef2f2',        // red-50
    borderColor: '#fecaca',    // red-200
    gaugePercent: 95,
  },
  2: {
    label: 'High Priority',
    captionColor: '#ea580c',   // orange-600 for caption text
    segmentColor: '#fb923c',   // orange-400 for gauge segments
    bgColor: '#fff7ed',        // orange-50
    borderColor: '#fed7aa',    // orange-200
    gaugePercent: 70,
  },
  3: {
    label: 'Medium Risk',
    captionColor: '#854d0e',   // yellow-800 for caption text
    segmentColor: '#fde047',   // yellow-300 (brand secondary) for gauge segments
    bgColor: '#fffbeb',        // amber-50
    borderColor: '#fde68a',    // amber-200
    gaugePercent: 45,
  },
  4: {
    label: 'Standard Risk',
    captionColor: '#16a34a',   // green-600 for caption text
    segmentColor: '#4ade80',   // green-400 for gauge segments
    bgColor: '#f0fdf4',        // green-50
    borderColor: '#bbf7d0',    // green-200
    gaugePercent: 20,
  },
};

// ============================================================================
// DOMAIN CONFIGURATION
// ============================================================================

/**
 * Configuration for risk domains.
 * 
 * Each domain represents a category of risk factors:
 * - Safety: Risk to self or others (highest priority)
 * - Function & Stability: Risk of catastrophic failure in a major life domain
 * - Vulnerability: Systemic factors that increase overall risk
 * 
 * The order determines display sequence in the UI.
 */
export const DOMAIN_CONFIG = {
  'Safety': {
    description: 'Risk to self or others',
    icon: ExclamationCircleIcon,
    order: 1,
  },
  'Function & Stability': {
    description: 'Risk of catastrophic failure in a major life domain',
    icon: ExclamationIcon,
    order: 2,
  },
  'Vulnerability': {
    description: 'Systemic factors that increase overall risk',
    icon: InformationCircleIcon,
    order: 3,
  },
};

// ============================================================================
// SCORE LABELS
// ============================================================================

/**
 * Score to label and styling mapping for individual indicators.
 * 
 * Maps numeric risk scores to display labels and colors:
 * - 4: Urgent (Red)
 * - 3: High (Orange)
 * - 2: Medium (Yellow)
 * - 1: Standard (Green)
 * - 0: Not Relevant (Gray) - Used for manually reviewed items deemed not relevant
 * - needsReview: Special badge for items pending manual review
 */
export const SCORE_LABELS = {
  4: { label: 'Urgent', captionColor: '#dc2626', segmentColor: '#ef4444', bg: '#fef2f2' },
  3: { label: 'High', captionColor: '#ea580c', segmentColor: '#fb923c', bg: '#fff7ed' },
  2: { label: 'Medium', captionColor: '#854d0e', segmentColor: '#fde047', bg: '#fffbeb' },
  1: { label: 'Standard', captionColor: '#16a34a', segmentColor: '#4ade80', bg: '#f0fdf4' },
  // Darker gray for "Not Relevant" to make it more distinct from unselected/neutral states
  0: { label: 'Not Relevant', captionColor: '#374151', segmentColor: '#6b7280', bg: '#e5e7eb' },
  // Special badge for items still pending manual review (not a score, used for display only)
  // Purple/violet theme for manual review items - distinct from risk levels
  'needsReview': { label: 'Needs Review', captionColor: '#7c3aed', segmentColor: '#a78bfa', bg: '#f5f3ff' },
};

// ============================================================================
// RISK LEVEL OPTIONS FOR MANUAL REVIEW
// ============================================================================

/**
 * Risk level options for manual review assignment.
 * 
 * Used when clinicians manually assign risk levels to answers that
 * couldn't be automatically scored (e.g., free-text "Other" responses).
 * 
 * Maps to scores: Urgent=4, High=3, Medium=2, Standard=1, Not relevant=0
 */
export const RISK_LEVEL_OPTIONS = [
  { level: 'Urgent', score: 4, bgColor: '#fef2f2', textColor: '#dc2626', borderColor: '#fecaca' },
  { level: 'High', score: 3, bgColor: '#fff7ed', textColor: '#ea580c', borderColor: '#fed7aa' },
  { level: 'Medium', score: 2, bgColor: '#fffbeb', textColor: '#854d0e', borderColor: '#fde68a' },
  { level: 'Standard', score: 1, bgColor: '#f0fdf4', textColor: '#16a34a', borderColor: '#bbf7d0' },
  // Darker gray for "Not relevant" to make selected state more distinct from unselected buttons
  { level: 'Not relevant', score: 0, bgColor: '#e5e7eb', textColor: '#374151', borderColor: '#9ca3af' },
];

// ============================================================================
// DIALOG DIMENSIONS
// ============================================================================

/**
 * Standard dimensions for the risk stratification dialog.
 * 
 * Used to ensure consistent sizing across different views and for
 * calculating available content height.
 */
export const DIALOG_DIMENSIONS = {
  /** Maximum viewport height ratio (65vh) */
  MAX_HEIGHT_RATIO: 0.65,
  /** Minimum viewport height ratio (50vh) */
  MIN_HEIGHT_RATIO: 0.50,
  /** Height of the dialog header in pixels */
  HEADER_HEIGHT: 65,
  /** Content padding in pixels (p-6 = 24px * 2) */
  CONTENT_PADDING: 48,
  /** Fallback height when window is unavailable */
  FALLBACK_HEIGHT: 400,
};

// ============================================================================
// FORM KEY TO TITLE MAPPING
// ============================================================================

/**
 * Mapping of form keys to human-readable titles.
 * 
 * Used to display form names in a user-friendly format.
 */
export const FORM_TITLES = {
  // Triage forms
  'theirEarlyLife': 'Their Early Life',
  'theirFamilyAndHomeLife': 'Their Family & Home Life',
  'theirLifestyle': 'Their Lifestyle',
  'theirHistoryWithTheLaw': 'Their History with the Law',
  'theirFamilyHistory': 'Their Family History',
  'theirMedicalHistory': 'Their Medical History',
  'theirMentalHealthHistory': 'Their Mental Health History',
  // Referral forms
  'requestForReferral': 'Request for Referral',
  'reasonForReferral': 'Reason for Referral',
  'educationHistory': 'Education History',
  'snapIV26': 'SNAP-IV 26',
  // HFP B-code forms (Body Functions)
  'hfpThinkingAndLearning': 'Thinking & Learning',
  'hfpLanguageAndCommunication': 'Language & Communication',
  'hfpMovementAndCoordination': 'Movement & Coordination',
  'hfpEnergyAndMotivation': 'Energy & Motivation',
  'hfpAttentionAndMemory': 'Attention & Memory',
  'hfpEmotionsAndRelationships': 'Emotions & Relationships',
  'hfpSensoryExperiences': 'Sensory Experiences',
  // HFP D-code form (Activities & Participation)
  'hfpDailyLifeParticipation': 'Daily Life Participation',
};

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Maps a risk score (1-4) to a risk level label.
 * 
 * Used to convert numeric scores to the labels that RmsGaugeChart expects.
 * 
 * @param {number} score - The numeric risk score (1-4)
 * @returns {string} The risk level label ('Urgent', 'High', 'Medium', or 'Standard')
 */
export const scoreToRiskLevel = (score) => {
  const mapping = {
    4: 'Urgent',
    3: 'High',
    2: 'Medium',
    1: 'Standard',
  };
  return mapping[score] || 'Standard';
};

/**
 * Maps a tier (1-4) to a risk level label for the overall gauge.
 * 
 * Tier 1 = Urgent, Tier 2 = High, Tier 3 = Medium, Tier 4 = Standard
 * 
 * @param {number} tier - The risk tier (1-4)
 * @returns {string} The risk level label
 */
export const tierToRiskLevel = (tier) => {
  const mapping = {
    1: 'Urgent',
    2: 'High',
    3: 'Medium',
    4: 'Standard',
  };
  return mapping[tier] || 'Standard';
};

/**
 * Converts a form key to a human-readable title.
 * 
 * First checks the FORM_TITLES mapping, then falls back to converting
 * camelCase to Title Case.
 * 
 * @param {string} formKey - The form key (e.g., 'theirMentalHealthHistory')
 * @returns {string} Human-readable title (e.g., 'Their Mental Health History')
 */
export const formKeyToTitle = (formKey) => {
  if (!formKey) return '';
  
  // Remove role suffix if present (e.g., '_guardian', '_school_user')
  const baseKey = formKey.replace(/_guardian|_school_user/g, '');
  
  // Return mapped title if available
  if (FORM_TITLES[baseKey]) {
    return FORM_TITLES[baseKey];
  }
  
  // Fallback: convert camelCase to Title Case
  return baseKey
    .replace(/([A-Z])/g, ' $1')
    .replace(/^./, str => str.toUpperCase())
    .trim();
};

/**
 * Calculates indicator statistics from domain summaries.
 * 
 * Aggregates counts across all domains and determines the highest risk level
 * present and its count. Used to display summary information like
 * "5 Urgent risk indicators reported".
 * 
 * @param {Object} domainSummaries - The domainSummaries object from risk data
 * @returns {Object} Object containing:
 *   - highestLevel: The highest risk level present ('Urgent', 'High', 'Medium', or null)
 *   - highestLevelCount: The count of indicators at that level
 *   - urgentCount: Total urgent indicators
 *   - highCount: Total high indicators
 *   - mediumCount: Total medium indicators
 */
export const calculateIndicatorStats = (domainSummaries) => {
  let urgentCount = 0;
  let highCount = 0;
  let mediumCount = 0;
  
  Object.values(domainSummaries || {}).forEach((summary) => {
    urgentCount += summary.urgentCount || 0;
    highCount += summary.severeCount || 0;
    mediumCount += summary.moderateCount || 0;
  });
  
  // Determine highest risk level and its count
  // Only return the count for the highest level present
  let highestLevel = null;
  let highestLevelCount = 0;
  
  if (urgentCount > 0) {
    highestLevel = 'Urgent';
    highestLevelCount = urgentCount;
  } else if (highCount > 0) {
    highestLevel = 'High';
    highestLevelCount = highCount;
  } else if (mediumCount > 0) {
    highestLevel = 'Medium';
    highestLevelCount = mediumCount;
  }
  
  return { highestLevel, highestLevelCount, urgentCount, highCount, mediumCount };
};

/**
 * Groups indicators by respondent type (guardian vs school_user).
 * 
 * Uses the respondentRole field from each indicator to categorize.
 * Triage forms are guardian-only, while referral forms can have
 * indicators from both guardian and school_user respondents.
 * 
 * @param {Array} indicators - Array of indicator objects
 * @returns {Object} Object with guardian and school arrays
 */
export const groupIndicatorsByRespondent = (indicators) => {
  const groups = {
    guardian: [],
    school: [],
  };
  
  (indicators || []).forEach((ind) => {
    const role = ind.respondentRole || 'guardian';
    if (role === 'school_user') {
      groups.school.push(ind);
    } else {
      groups.guardian.push(ind);
    }
  });
  
  return groups;
};

/**
 * Sorts domains by their configured order.
 * 
 * @param {Array} domainEntries - Array of [domainName, summary] entries
 * @returns {Array} Sorted array of domain entries
 */
export const sortDomainsByOrder = (domainEntries) => {
  return [...domainEntries].sort(([a], [b]) => {
    const orderA = DOMAIN_CONFIG[a]?.order || 99;
    const orderB = DOMAIN_CONFIG[b]?.order || 99;
    return orderA - orderB;
  });
};

/**
 * Gets the appropriate label for a respondent role.
 * 
 * @param {string} role - The respondent role ('guardian' or 'school_user')
 * @param {Object} respondentNames - Optional object with actual names { guardian: {...}, school_user: {...} }
 * @param {string} defaultGuardian - Default label for guardian (default: 'Parent / Carer')
 * @param {string} defaultSchool - Default label for school (default: 'School Representative')
 * @returns {string} The display label for the respondent
 */
export const getRespondentLabel = (role, respondentNames, defaultGuardian = 'Parent / Carer', defaultSchool = 'School Representative') => {
  if (role === 'school_user') {
    const schoolName = respondentNames?.school_user;
    if (schoolName?.firstName || schoolName?.lastName) {
      return `${schoolName.firstName || ''} ${schoolName.lastName || ''}`.trim();
    }
    return defaultSchool;
  }
  
  // Guardian role
  const guardianName = respondentNames?.guardian;
  if (guardianName?.firstName || guardianName?.lastName) {
    return `${guardianName.firstName || ''} ${guardianName.lastName || ''}`.trim();
  }
  return defaultGuardian;
};

/**
 * Calculates the maximum content height for the dialog based on viewport.
 * 
 * @returns {number} The maximum content height in pixels
 */
export const calculateMaxContentHeight = () => {
  if (typeof window === 'undefined') {
    return DIALOG_DIMENSIONS.FALLBACK_HEIGHT;
  }
  return Math.floor(window.innerHeight * DIALOG_DIMENSIONS.MAX_HEIGHT_RATIO) 
    - DIALOG_DIMENSIONS.HEADER_HEIGHT;
};

/**
 * Calculates the available content height within the dialog.
 * 
 * Subtracts header and padding from the max viewport height.
 * 
 * @returns {number} The available content height in pixels
 */
export const calculateAvailableHeight = () => {
  if (typeof window === 'undefined') {
    return DIALOG_DIMENSIONS.FALLBACK_HEIGHT;
  }
  return Math.floor(window.innerHeight * DIALOG_DIMENSIONS.MAX_HEIGHT_RATIO) 
    - DIALOG_DIMENSIONS.HEADER_HEIGHT 
    - DIALOG_DIMENSIONS.CONTENT_PADDING;
};

/**
 * Gets badge style based on risk level for inline styles.
 * 
 * @param {string} level - The risk level ('Urgent', 'High', 'Medium', 'Standard', or 'Needs Review')
 * @returns {Object} Style object for the badge
 */
export const getBadgeStyleForLevel = (level) => {
  const levelLower = (level || '').toLowerCase();
  
  const styles = {
    'urgent': {
      backgroundColor: '#fef2f2',
      color: '#dc2626',
    },
    'high': {
      backgroundColor: '#fff7ed',
      color: '#ea580c',
    },
    'medium': {
      backgroundColor: '#fffbeb',
      color: '#854d0e',
    },
    'standard': {
      backgroundColor: '#f0fdf4',
      color: '#16a34a',
    },
    'not relevant': {
      backgroundColor: '#e5e7eb',  // gray-200 - darker for visibility
      color: '#374151',            // gray-700 - darker text
    },
    'needs review': {
      backgroundColor: '#f5f3ff',  // violet-50
      color: '#7c3aed',            // violet-600
    },
  };
  
  return styles[levelLower] || styles['standard'];
};
